<script lang="ts" setup>
import {ref} from 'vue';
import {trackEvent} from '../../components/app/analytics';
import {getStrings} from '../../core/lang';
import {httpService} from '../../core/services/http.service';
import strings from '../auth.strings.yaml';

const S = getStrings<AuthStrings>(strings);

const props = defineProps<{
    enforceEvoMigration: boolean;
}>();

const emit = defineEmits<{
    skip: [];
    error: [error: any];
}>();

const migrating = ref(false);

async function migrate() {
    trackEvent('action', 'evo-migration-link', null);
    migrating.value = true;
    try {
        const response = await httpService.get('/ui-api/migration/url');
        const migrationUrl = response.data?.migrationUrl;
        if (migrationUrl) {
            window.location.href = migrationUrl;
        }
    } catch (error) {
        migrating.value = false;
        emit('error', error);
    }
}

function skip() {
    emit('skip');
}
</script>

<template>
    <div class="text-[#1b1c41]">
        <h1 class="mb-3 text-[32px] font-semibold leading-tight">{{ S.evoMigrationTitle }}</h1>
        <p class="mb-6 text-base leading-6">{{ S.evoMigrationIntro }}</p>

        <ol class="mb-7 list-none space-y-4 p-0">
            <li class="flex items-start gap-3">
                <span
                    class="mt-1 size-7 shrink-0 rounded-full bg-[#1b1c41] text-center text-sm font-semibold leading-7 text-white"
                    aria-hidden="true"
                >
                    1
                </span>
                <span class="text-base">{{ S.evoMigrationStep1 }}</span>
            </li>
            <li class="flex items-start gap-3">
                <span
                    class="mt-1 size-7 shrink-0 rounded-full bg-[#1b1c41] text-center text-sm font-semibold leading-7 text-white"
                    aria-hidden="true"
                >
                    2
                </span>
                <span class="text-base">{{ S.evoMigrationStep2 }}</span>
            </li>
            <li class="flex items-start gap-3">
                <span
                    class="mt-1 size-7 shrink-0 rounded-full bg-[#1b1c41] text-center text-sm font-semibold leading-7 text-white"
                    aria-hidden="true"
                >
                    3
                </span>
                <span class="text-base underline-offset-2">
                    {{ S.evoMigrationStep3 }}
                </span>
            </li>
        </ol>

        <div class="flex flex-col gap-2.5">
            <button
                type="button"
                :disabled="migrating"
                class="box-border flex h-11 min-h-[44px] w-full cursor-pointer items-center justify-center gap-2 rounded border-0 bg-[#1b1c41] px-3 py-2 text-base leading-3 text-white active:bg-[hsl(238,31%,21%)] disabled:cursor-not-allowed disabled:opacity-60"
                @click="migrate"
            >
                <div
                    v-if="migrating"
                    class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-solid border-white border-t-transparent"
                />
                {{ S.evoMigrationLinkCta }}
            </button>
            <button
                v-if="!enforceEvoMigration"
                type="button"
                class="box-border flex h-11 min-h-[44px] w-full cursor-pointer items-center justify-center rounded border border-solid border-[#1b1c41] bg-white px-3 py-2 text-base leading-3 text-[#1b1c41] active:bg-gray-100"
                @click="skip"
            >
                {{ S.evoMigrationSkip }}
            </button>
        </div>
    </div>
</template>
